import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Mail,
  HeartHandshake,
  Building2,
  TrendingUp,
  Megaphone,
  Newspaper,
  Briefcase,
  HelpCircle,
  Send,
  Loader2,
  CheckCircle2,
  ChevronRight,
  MapPin,
  Hospital,
  ShieldCheck,
  Users,
  FileText,
} from "lucide-react";

// ── Design tokens (shared with other AHP pages) ────────────────────────────
const T_LIGHT = {
  bg: "#FAF9F6",
  text: "#1B1B1B",
  muted: "#6b7280",
  navy: "#002868",
  red: "#B22234",
  gold: "#C5A55A",
  green: "#2b5e49",
  cardBg: "#ffffff",
  border: "rgba(0,0,0,0.08)",
  inputBg: "#FAF9F6",
};
const T_DARK = {
  bg: "#0a1628",
  text: "#f0ebe3",
  muted: "#9ca3af",
  navy: "#7da6d4",
  red: "#e85d6c",
  gold: "#C5A55A",
  green: "#6bc9a0",
  cardBg: "#111827",
  border: "rgba(255,255,255,0.08)",
  inputBg: "#0a1628",
};

const serif = "'Instrument Serif', Georgia, serif";
const sans = "'Instrument Sans', system-ui, sans-serif";

// ── Contact departments ─────────────────────────────────────────────────────
type Department = {
  id: string;
  label: string;
  email: string;
  icon: React.ElementType;
  color: (t: typeof T_LIGHT) => string;
  description: string;
  fields: FormField[];
};

type FormField = {
  name: string;
  label: string;
  type: "text" | "email" | "tel" | "textarea" | "select";
  placeholder: string;
  required: boolean;
  options?: string[];
};

const COMMON_FIELDS: FormField[] = [
  { name: "name", label: "Full Name", type: "text", placeholder: "Your full name", required: true },
  { name: "email", label: "Email", type: "email", placeholder: "you@example.com", required: true },
];

const DEPARTMENTS: Department[] = [
  {
    id: "support",
    label: "Customer Support",
    email: "support@americanhospitalprices.com",
    icon: HelpCircle,
    color: (t) => t.green,
    description: "Need help with your subscription, an order, or using the platform? We're here to help.",
    fields: [
      ...COMMON_FIELDS,
      {
        name: "topic",
        label: "What do you need help with?",
        type: "select",
        placeholder: "Select a topic",
        required: true,
        options: [
          "Report purchase ($0.99)",
          "Monthly subscription ($4.99/mo)",
          "Account & login issues",
          "Data accuracy question",
          "Feature request",
          "Bug report",
          "Other",
        ],
      },
      { name: "order_id", label: "Order / Transaction ID (if applicable)", type: "text", placeholder: "e.g. pi_3abc...", required: false },
      { name: "message", label: "Describe your issue", type: "textarea", placeholder: "Tell us what happened and how we can help...", required: true },
    ],
  },
  {
    id: "hospital",
    label: "Hospitals & Data Providers",
    email: "data@americanhospitalprices.com",
    icon: Hospital,
    color: (t) => t.navy,
    description: "Represent a hospital or health system? Submit or update your chargemaster data, dispute a listing, or request corrections.",
    fields: [
      ...COMMON_FIELDS,
      { name: "organization", label: "Hospital / Organization Name", type: "text", placeholder: "e.g. Memorial Regional Medical Center", required: true },
      { name: "title", label: "Your Title / Role", type: "text", placeholder: "e.g. Director of Revenue Cycle", required: true },
      { name: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 123-4567", required: false },
      {
        name: "topic",
        label: "Reason for contact",
        type: "select",
        placeholder: "Select a reason",
        required: true,
        options: [
          "Submit chargemaster / MRF file",
          "Update existing pricing data",
          "Dispute a listing or grade",
          "Request data correction",
          "Partnership inquiry",
          "Other",
        ],
      },
      { name: "message", label: "Details", type: "textarea", placeholder: "Provide details about your request. If submitting a chargemaster file, include the URL where your file is hosted...", required: true },
    ],
  },
  {
    id: "investor",
    label: "Investors",
    email: "investor@americanhospitalprices.com",
    icon: TrendingUp,
    color: (t) => t.gold,
    description: "Interested in funding the fight for healthcare price transparency? We're building the platform to make it happen.",
    fields: [
      ...COMMON_FIELDS,
      { name: "organization", label: "Firm / Fund Name", type: "text", placeholder: "e.g. Sequoia Capital", required: false },
      { name: "title", label: "Your Title", type: "text", placeholder: "e.g. Partner", required: false },
      { name: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 123-4567", required: false },
      {
        name: "topic",
        label: "Investment type",
        type: "select",
        placeholder: "Select type",
        required: true,
        options: [
          "Angel investment",
          "Seed round",
          "Series A+",
          "Strategic investment",
          "Grant / foundation funding",
          "Just exploring",
        ],
      },
      { name: "message", label: "Tell us about your interest", type: "textarea", placeholder: "What drew you to American Hospital Prices? Any specific questions about our model, traction, or vision?", required: true },
    ],
  },
  {
    id: "careers",
    label: "Careers & Volunteers",
    email: "careers@americanhospitalprices.com",
    icon: Megaphone,
    color: (t) => t.red,
    description: "Join the movement. We need activists in every state to manage social media, call out price-gouging hospitals, and organize locally.",
    fields: [
      ...COMMON_FIELDS,
      { name: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 123-4567", required: false },
      {
        name: "state",
        label: "Your State",
        type: "select",
        placeholder: "Select your state",
        required: true,
        options: [
          "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY",
          "LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH",
          "OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
        ],
      },
      {
        name: "topic",
        label: "What role interests you?",
        type: "select",
        placeholder: "Select a role",
        required: true,
        options: [
          "State Social Media Manager (X/Twitter)",
          "State Social Media Manager (Instagram/TikTok)",
          "City/Metro Social Media Manager",
          "Community Organizer / Local Activist",
          "Data Researcher / Analyst",
          "Software Engineer (Frontend)",
          "Software Engineer (Backend/Data)",
          "Content Writer / Journalist",
          "Graphic Designer / Video Editor",
          "Legal (Healthcare Law)",
          "Other / General Volunteer",
        ],
      },
      { name: "message", label: "Tell us about yourself", type: "textarea", placeholder: "What skills do you bring? Why does healthcare price transparency matter to you? Include links to your portfolio, social accounts, or resume if you have them.", required: true },
    ],
  },
  {
    id: "press",
    label: "Media & Press",
    email: "press@americanhospitalprices.com",
    icon: Newspaper,
    color: (t) => t.navy,
    description: "Covering healthcare pricing, hospital transparency, or medical billing? We have data and stories for you.",
    fields: [
      ...COMMON_FIELDS,
      { name: "organization", label: "Publication / Outlet", type: "text", placeholder: "e.g. The New York Times", required: true },
      { name: "title", label: "Your Title", type: "text", placeholder: "e.g. Health Reporter", required: false },
      { name: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 123-4567", required: false },
      {
        name: "topic",
        label: "Type of inquiry",
        type: "select",
        placeholder: "Select type",
        required: true,
        options: [
          "Interview request",
          "Data request for story",
          "Fact check / verification",
          "Press kit / brand assets",
          "Podcast / panel invitation",
          "Other",
        ],
      },
      { name: "deadline", label: "Deadline (if any)", type: "text", placeholder: "e.g. March 1, 2026", required: false },
      { name: "message", label: "Details", type: "textarea", placeholder: "What's the story? What data or quotes do you need? We respond to press inquiries within 24 hours.", required: true },
    ],
  },
  {
    id: "sales",
    label: "Sales & Partnerships",
    email: "sales@americanhospitalprices.com",
    icon: Briefcase,
    color: (t) => t.green,
    description: "Interested in API access, white-label data, or integrating our pricing data into your product? Let's talk.",
    fields: [
      ...COMMON_FIELDS,
      { name: "organization", label: "Company Name", type: "text", placeholder: "Your company name", required: true },
      { name: "title", label: "Your Title", type: "text", placeholder: "e.g. VP of Product", required: false },
      { name: "phone", label: "Phone Number", type: "tel", placeholder: "(555) 123-4567", required: false },
      {
        name: "topic",
        label: "What are you looking for?",
        type: "select",
        placeholder: "Select type",
        required: true,
        options: [
          "API access / data licensing",
          "White-label solution",
          "Affiliate / referral partnership",
          "Integration partnership",
          "Sponsorship",
          "Other",
        ],
      },
      { name: "message", label: "Tell us about your needs", type: "textarea", placeholder: "Describe your use case, expected volume, and timeline...", required: true },
    ],
  },
  {
    id: "general",
    label: "General Inquiry",
    email: "hello@americanhospitalprices.com",
    icon: Mail,
    color: (t) => t.muted,
    description: "Don't fit into the categories above? Drop us a line and we'll route you to the right team.",
    fields: [
      ...COMMON_FIELDS,
      { name: "subject", label: "Subject", type: "text", placeholder: "What's this about?", required: true },
      { name: "message", label: "Message", type: "textarea", placeholder: "How can we help?", required: true },
    ],
  },
];

// ── Form component for a single department ──────────────────────────────────
function DepartmentForm({
  dept,
  T,
  isDark,
}: {
  dept: Department;
  T: typeof T_LIGHT;
  isDark: boolean;
}) {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      // Store in Supabase
      const { error } = await supabase.from("contact_submissions").insert({
        department: dept.id,
        email_to: dept.email,
        name: formData.name || null,
        email_from: formData.email || null,
        phone: formData.phone || null,
        organization: formData.organization || null,
        subject: formData.subject || formData.topic || dept.label,
        message: formData.message || null,
        metadata: formData,
      });

      if (error) throw error;
      setStatus("sent");
      setFormData({});
    } catch (err: any) {
      console.error("Contact form error:", err);
      setStatus("error");
      setErrorMsg(
        "Something went wrong. Please try emailing us directly at " + dept.email
      );
    }
  };

  if (status === "sent") {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 px-6 text-center rounded-xl"
        style={{ backgroundColor: T.cardBg, border: `1px solid ${T.border}` }}
      >
        <CheckCircle2
          className="w-12 h-12 mb-4"
          style={{ color: T.green }}
        />
        <h3
          className="text-xl font-bold mb-2"
          style={{ fontFamily: serif, color: T.text }}
        >
          Message Sent
        </h3>
        <p
          className="text-sm mb-6 max-w-md"
          style={{ fontFamily: sans, color: T.muted }}
        >
          We've received your message and will respond within 1-2 business days.
          For urgent matters, email us directly at{" "}
          <a
            href={`mailto:${dept.email}`}
            className="underline"
            style={{ color: T.navy }}
          >
            {dept.email}
          </a>
          .
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="text-sm font-medium px-6 py-2 rounded-lg transition-opacity hover:opacity-80"
          style={{
            backgroundColor: `${T.navy}10`,
            color: T.navy,
            fontFamily: sans,
          }}
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {dept.fields.map((field) => (
        <div key={field.name}>
          <label
            className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ fontFamily: sans, color: T.muted }}
          >
            {field.label}
            {field.required && (
              <span style={{ color: T.red }}> *</span>
            )}
          </label>

          {field.type === "textarea" ? (
            <textarea
              value={formData[field.name] || ""}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              rows={5}
              className="w-full px-4 py-3 rounded-lg text-sm outline-none resize-y"
              style={{
                backgroundColor: T.inputBg,
                border: `1px solid ${T.border}`,
                color: T.text,
                fontFamily: sans,
                minHeight: 100,
              }}
            />
          ) : field.type === "select" ? (
            <select
              value={formData[field.name] || ""}
              onChange={(e) => handleChange(field.name, e.target.value)}
              required={field.required}
              className="w-full px-4 py-3 rounded-lg text-sm outline-none"
              style={{
                backgroundColor: T.inputBg,
                border: `1px solid ${T.border}`,
                color: formData[field.name] ? T.text : T.muted,
                fontFamily: sans,
              }}
            >
              <option value="">{field.placeholder}</option>
              {field.options?.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={field.type}
              value={formData[field.name] || ""}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              className="w-full px-4 py-3 rounded-lg text-sm outline-none"
              style={{
                backgroundColor: T.inputBg,
                border: `1px solid ${T.border}`,
                color: T.text,
                fontFamily: sans,
              }}
            />
          )}
        </div>
      ))}

      {status === "error" && (
        <p className="text-sm" style={{ color: T.red, fontFamily: sans }}>
          {errorMsg}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="w-full py-3 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
        style={{ backgroundColor: T.navy, fontFamily: sans }}
      >
        {status === "sending" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            Send Message
          </>
        )}
      </button>

      <p
        className="text-xs text-center"
        style={{ color: T.muted, fontFamily: sans }}
      >
        Or email directly:{" "}
        <a
          href={`mailto:${dept.email}`}
          className="underline"
          style={{ color: T.navy }}
        >
          {dept.email}
        </a>
      </p>
    </form>
  );
}

// ── Main Contact page ───────────────────────────────────────────────────────
const Contact = () => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const T = isDark ? T_DARK : T_LIGHT;
  // ?dept=<id> preselects a department so other pages can deep-link straight into
  // the right form — e.g. the compliance CTA on /lawsuits-against-us, which used to
  // be a mailto. Falls back to support if the id isn't one we know, so a stale or
  // hand-edited link degrades to a working form rather than a crash.
  const [searchParams] = useSearchParams();
  const deptParam = searchParams.get("dept");
  const [activeDept, setActiveDept] = useState<string>(
    DEPARTMENTS.some((d) => d.id === deptParam) ? (deptParam as string) : "support"
  );

  const currentDept = DEPARTMENTS.find((d) => d.id === activeDept)!;
  const Icon = currentDept.icon;

  return (
    <div
      className="min-h-screen pb-24 md:pb-8"
      style={{ backgroundColor: T.bg, color: T.text }}
    >
      <Helmet>
        <title>Contact Us | {brand.name}</title>
        <meta
          name="description"
          content="Get in touch with American Hospital Prices. Customer support, hospital data submissions, investor inquiries, career opportunities, press contacts, and more."
        />
        <link rel="canonical" href={`${brand.primaryDomain}/contact`} />
        <meta property="og:title" content={`Contact Us | ${brand.name}`} />
        <meta
          property="og:description"
          content="Get in touch with American Hospital Prices for support, partnerships, press, careers, and more."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${brand.primaryDomain}/contact`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: `Contact ${brand.name}`,
            url: `${brand.primaryDomain}/contact`,
            mainEntity: {
              "@type": "Organization",
              name: brand.name,
              url: brand.primaryDomain,
              contactPoint: DEPARTMENTS.map((d) => ({
                "@type": "ContactPoint",
                contactType: d.label,
                email: d.email,
              })),
            },
          })}
        </script>
      </Helmet>

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO
      ═══════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${isDark ? "#0a1628" : "#002868"} 0%, ${isDark ? "#060e1a" : "#001540"} 100%)`,
        }}
      >
        <div className="mx-auto max-w-4xl px-6 py-14 md:py-18 text-center relative z-10">
          <div
            className="mx-auto mb-5 w-16 h-16 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: `${T.gold}20`,
              border: `2px solid ${T.gold}50`,
            }}
          >
            <Mail className="w-8 h-8" style={{ color: T.gold }} />
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold mb-3"
            style={{ fontFamily: serif, color: "#fff" }}
          >
            Get In Touch
          </h1>
          <p
            className="text-base md:text-lg max-w-2xl mx-auto"
            style={{ fontFamily: sans, color: "rgba(255,255,255,0.75)" }}
          >
            Whether you're a patient, hospital, investor, journalist, or
            activist — we want to hear from you.
          </p>
        </div>
        <div
          className="h-1"
          style={{
            background: `linear-gradient(to right, ${T.red}, ${T.red} 33%, white 33%, white 66%, ${T.red} 66%)`,
          }}
        />
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          DEPARTMENT SELECTOR + FORM
      ═══════════════════════════════════════════════════════════════════════ */}
      {/* id is the scroll target for the "All Departments" tiles further down the
          page, which select a department and bring the visitor back up here. */}
      <section id="contact-form-anchor" className="mx-auto max-w-5xl px-4 md:px-6 py-10">
        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          {/* ── Left sidebar: department tabs ── */}
          <div className="space-y-2">
            <h2
              className="text-xs font-bold uppercase tracking-widest mb-3 px-1"
              style={{ fontFamily: sans, color: T.muted }}
            >
              Who do you need?
            </h2>
            {DEPARTMENTS.map((dept) => {
              const DeptIcon = dept.icon;
              const isActive = activeDept === dept.id;
              const accentColor = dept.color(T);

              return (
                <button
                  key={dept.id}
                  onClick={() => setActiveDept(dept.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all"
                  style={{
                    backgroundColor: isActive
                      ? `${accentColor}${isDark ? "18" : "0C"}`
                      : "transparent",
                    border: isActive
                      ? `1px solid ${accentColor}${isDark ? "40" : "25"}`
                      : `1px solid transparent`,
                    color: isActive ? accentColor : T.text,
                  }}
                >
                  <DeptIcon
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: isActive ? accentColor : T.muted }}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-sm font-semibold leading-tight"
                      style={{ fontFamily: sans }}
                    >
                      {dept.label}
                    </div>
                    <div
                      className="text-[11px] truncate"
                      style={{
                        fontFamily: sans,
                        color: isActive
                          ? `${accentColor}99`
                          : T.muted,
                      }}
                    >
                      {dept.email}
                    </div>
                  </div>
                  {isActive && (
                    <ChevronRight
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: accentColor }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Right side: active form ── */}
          <div>
            {/* Department header */}
            <div
              className="rounded-xl p-6 mb-6"
              style={{
                backgroundColor: T.cardBg,
                border: `1px solid ${T.border}`,
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: `${currentDept.color(T)}15`,
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{ color: currentDept.color(T) }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h2
                    className="text-xl font-bold mb-1"
                    style={{ fontFamily: serif, color: T.text }}
                  >
                    {currentDept.label}
                  </h2>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ fontFamily: sans, color: T.muted }}
                  >
                    {currentDept.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div
              className="rounded-xl p-6"
              style={{
                backgroundColor: T.cardBg,
                border: `1px solid ${T.border}`,
              }}
            >
              <DepartmentForm
                key={currentDept.id}
                dept={currentDept}
                T={T}
                isDark={isDark}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          QUICK CONTACT CARDS — All Departments at a Glance
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-5xl px-4 md:px-6 pb-10">
        <h2
          className="text-2xl font-bold mb-6 text-center"
          style={{ fontFamily: serif, color: T.text }}
        >
          All Departments
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {DEPARTMENTS.map((dept) => {
            const DeptIcon = dept.icon;
            const color = dept.color(T);
            return (
              // Was href={`mailto:${dept.email}`} until 2026-08-02. This page already
              // has a working form that writes to contact_submissions, so sending the
              // department grid out to a mail client was strictly worse: for most
              // visitors mailto opens nothing, and for anyone with webmail registered
              // as a protocol handler it opens a compose window on another domain.
              // The tiles now do what the picker above them does — select the
              // department and put the visitor in the form.
              <button
                key={dept.id}
                type="button"
                onClick={() => {
                  setActiveDept(dept.id);
                  document.getElementById("contact-form-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="flex flex-col items-center gap-2 rounded-xl p-4 text-center transition-all hover:shadow-md group cursor-pointer"
                style={{
                  backgroundColor: T.cardBg,
                  border: `1px solid ${T.border}`,
                }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${color}12` }}
                >
                  <DeptIcon className="w-5 h-5" style={{ color }} />
                </div>
                <span
                  className="text-xs font-semibold"
                  style={{ fontFamily: sans, color: T.text }}
                >
                  {dept.label}
                </span>
                <span
                  className="text-[10px] group-hover:underline"
                  style={{ fontFamily: sans, color: T.muted }}
                >
                  {dept.email}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          CAREERS / ACTIVIST CTA
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-5xl px-4 md:px-6 pb-10">
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${T.border}` }}
        >
          <div
            className="p-6 md:p-8"
            style={{
              background: `linear-gradient(135deg, ${isDark ? "#1a0a0a" : T.red} 0%, ${isDark ? "#0a1628" : "#8B1A28"} 100%)`,
            }}
          >
            <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
              >
                <Megaphone
                  className="w-6 h-6"
                  style={{ color: "#fff" }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <span
                  className="text-xs font-bold uppercase tracking-widest"
                  style={{ color: T.gold, fontFamily: sans }}
                >
                  Join The Movement
                </span>
                <h3
                  className="text-xl md:text-2xl font-bold text-white mt-1 mb-3"
                  style={{ fontFamily: serif }}
                >
                  We Need Activists In Every State
                </h3>
                <p
                  className="text-sm leading-relaxed mb-4"
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    fontFamily: sans,
                  }}
                >
                  American Hospital Prices runs social media accounts in all
                  50 states. We're looking for passionate people to manage
                  these accounts — posting about local hospital pricing,
                  calling out non-compliant facilities, and organizing
                  community action. This is a real role with real impact.
                </p>
                <div className="flex flex-wrap gap-3 mb-4">
                  {[
                    { icon: MapPin, text: "Work in your state" },
                    { icon: Users, text: "Join a national team" },
                    { icon: ShieldCheck, text: "Make hospitals accountable" },
                    { icon: FileText, text: "Data-driven advocacy" },
                  ].map((item) => (
                    <div
                      key={item.text}
                      className="flex items-center gap-1.5 text-xs font-medium"
                      style={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      {item.text}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setActiveDept("careers");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: T.gold,
                    color: "#1B1B1B",
                    fontFamily: sans,
                  }}
                >
                  Apply Now <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          FAQ
      ═══════════════════════════════════════════════════════════════════════ */}
      <section className="mx-auto max-w-3xl px-4 md:px-6 pb-16">
        <h2
          className="text-2xl font-bold mb-6 text-center"
          style={{ fontFamily: serif, color: T.text }}
        >
          Common Questions
        </h2>
        {[
          {
            q: "How quickly do you respond?",
            a: "We aim to respond to all inquiries within 1-2 business days. Press inquiries receive priority response within 24 hours. Customer support issues are typically resolved same-day.",
          },
          {
            q: "I'm a hospital — how do I update my pricing data?",
            a: "Select 'Hospitals & Data Providers' above and submit your updated chargemaster file URL. We process updates within 5-7 business days. You can also email data@americanhospitalprices.com directly with your file link.",
          },
          {
            q: "What does a State Social Media Manager do?",
            a: "You manage our social media presence for your state — posting about local hospital pricing stories, compliance alerts, and legislative updates. We provide the data, graphics, and guidelines. You bring the local knowledge and passion for accountability.",
          },
          {
            q: "Is volunteering paid?",
            a: "We're a bootstrapped startup fighting for healthcare transparency. Most roles start as volunteer positions with plans to compensate as revenue grows. Key roles like State Social Media Managers may receive stipends or revenue share.",
          },
          {
            q: "I need help with my subscription / order",
            a: "Select 'Customer Support' above and include your order or transaction ID if you have one. For immediate help, email support@americanhospitalprices.com with your issue details.",
          },
        ].map((faq, i) => (
          <div
            key={i}
            className="py-4"
            style={
              i > 0
                ? { borderTop: `1px solid ${T.border}` }
                : undefined
            }
          >
            <h3
              className="font-bold mb-1.5 text-sm"
              style={{ fontFamily: serif, color: T.text }}
            >
              {faq.q}
            </h3>
            <p
              className="text-sm leading-relaxed"
              style={{ fontFamily: sans, color: T.muted }}
            >
              {faq.a}
            </p>
          </div>
        ))}
      </section>
    </div>
  );
};

export default Contact;
